import type { Recipe } from '../types/recipe';
import {
  coerceInstructionSections,
  normalizeInstructions,
  type NormalizedInstructions,
} from './normalizeInstructions';

/**
 * How the recipe detail screen turns a recipe into the steps it renders.
 *
 * Extracted from `app/(app)/recipe-detail/[id].tsx` so the rule below can be
 * tested — jest's roots do not include `app/`.
 *
 * The screen gates the whole steps section on `steps.length > 0` and nothing
 * else, so this function must never return an empty flat list while sections
 * carry real steps: that combination rendered "Aucune instruction ajoutée" on
 * top of a perfectly good recipe (#678). `getCookingSteps` (./cookingSteps)
 * applies the same precedence for cooking mode and the planning timeline, which
 * is what keeps the three screens on one step count.
 *
 * Text is left exactly as stored — `RecipeCookingSteps` strips HTML and leading
 * numbering at render time, and the step-ingredient chips are keyed on the raw
 * text, so cleaning it here would silently drop every chip.
 */
export interface DetailStep {
  number: number;
  step: string;
  tip?: string;
  ingredients?: string[];
}

export interface DetailStepSection {
  name?: string;
  steps: DetailStep[];
}

export interface DetailSteps {
  steps: DetailStep[];
  sections?: DetailStepSection[];
}

export function buildDetailSteps(
  recipe: Pick<Recipe, 'instructions' | 'instructionSections'> | null | undefined,
  enrichStep: (text: string) => string[],
  /** Pass the screen's already-computed normalization so it is not redone. */
  normalized: NormalizedInstructions = normalizeInstructions(recipe?.instructions),
): DetailSteps {
  const coercedSections = coerceInstructionSections(recipe?.instructionSections);

  if (coercedSections.some(s => s.name)) {
    // Upstream provides named sections — use them as-is.
    const sections: DetailStepSection[] = coercedSections.map(section => ({
      name: section.name,
      steps: section.steps.map(step => ({
        number: step.number,
        step: step.step,
        tip: undefined,
        ingredients: enrichStep(step.step),
      })),
    }));

    const flat = (recipe?.instructions ?? [])
      .map((step, index) => ({
        number: step.number || index + 1,
        step: step.step || '',
        tip: undefined,
        ingredients: enrichStep(step.step || ''),
      }))
      // Trimmed, not just truthy: a whitespace-only entry is not a step, and a
      // list of them would keep the sections fallback below from ever firing.
      .filter(s => s.step.trim().length > 0);

    if (flat.length > 0) return { steps: flat, sections };

    // The flat list is empty but the sections are not. Flatten them rather than
    // letting the screen's gate fail — this is the shape a stale translation
    // produces, and the shape #595's string-typed `steps` produce.
    let number = 0;
    return {
      steps: sections.flatMap(section =>
        section.steps.map(step => ({
          number: (number += 1),
          step: step.step,
          tip: undefined,
          ingredients: step.ingredients,
        })),
      ),
      sections,
    };
  }

  const steps = normalized.steps.map(s => ({
    number: s.number,
    step: s.step,
    tip: undefined,
    ingredients: enrichStep(s.step),
  }));

  if (!normalized.hasSections) return { steps };

  return {
    steps,
    sections: normalized.sections.map(section => ({
      name: section.name,
      steps: section.steps.map(s => ({
        number: s.number,
        step: s.step,
        tip: undefined,
        ingredients: enrichStep(s.step),
      })),
    })),
  };
}

export default buildDetailSteps;
